/** Tutor form helpers: DTO builders, hydration, validation. */
import {
  serializeProfessionalSituation,
  flattenProfessionalSituation,
} from '@/utils/professional-situation-utils';
import {
  ENGAGEMENT_DURATION_OPTIONS,
  TUTOR_ACCOUNT_STATUS_CONFIG,
  TUTOR_MIN_AGE,
} from '@/components/tutors/tutor-configs';
import { TUTOR_SITUATION_CONFIG } from '@/constants/shared-catalog';
import {
  parseLocalDateString,
  toCalendarDateString,
  toLocalDateString,
} from '@/utils/format-date-utils';
import { getCompanyCountry } from '@/stores/themeStore';
import {
  formBool,
  hydratePhoneFromApi,
  hydrateUserContactFields,
  phoneToE164,
  primaryContactKindFromType,
  assignPrimaryContactFields,
} from '@/utils/user-contact-utils';
import type { AddressSelectionResult } from '@/types/geography.types';
import type { TutorRecord, TutorSituation } from '@/types/remote/tutor.types';
import { updateCriminalRecordDecision } from '@/services/users.service';
import { uploadFile } from '@/services/files.service';
import { getErrorMessage } from '@/utils/errors-utils';
import { getPrimaryRole } from '@/utils/primary-role-utils';
import type { ArchiveReason, ArchiveRemoteUserDto, ReactivationPreview } from '@/types/user.types';
import {
  addressFieldsFromSelection,
  assignDefined,
  assignTruthy,
  buildArchiveDto as buildSharedArchiveDto,
  fileNameFromUrl as fileNameFromSharedUrl,
  formatReactivationPreview as formatSharedReactivationPreview,
  isArchiveReason as isSharedArchiveReason,
  parseDepartmentFromLabel,
  setNestedPath as setSharedNestedPath,
} from '@/utils/common-utils';
import { storedDocumentLink } from '@/utils/pending-document-link-utils';

export { formatYearsOfEngagement } from '@/utils/common-utils';

/** Form fields that hold uploaded document URLs. */
type DocumentLinkFields = {
  contractDocumentLink?: string;
  certDocumentLink?: string;
  criminalRecordDocumentLink?: string;
};

/** Last path segment of a stored file URL. */
export function fileNameFromUrl(url: string): string {
  return fileNameFromSharedUrl(url);
}

/** Immutable set of a dotted path on a form object. */
export function setNestedPath<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): T {
  return setSharedNestedPath(obj, path, value);
}

/** Archive DTO from a reason + comment. */
export function buildArchiveDto(reason: ArchiveReason, comment: string): ArchiveRemoteUserDto {
  return buildSharedArchiveDto(reason, comment);
}

/** True when the value is a known archive reason. */
export function isArchiveReason(value: string): value is ArchiveReason {
  return isSharedArchiveReason(value);
}

/** Upload a file; returns the stored URL. */
export function defaultUpload(file: File): Promise<string> {
  return uploadFile(file);
}

/** Form patch for a contract upload (or clear when `url` is empty). */
export function buildContractPatch(url: string): Record<string, string> {
  if (url)
    return {
      contractDocumentLink: url,
      contractDocumentName: fileNameFromSharedUrl(url),
      contractUploadDate: new Date().toISOString().slice(0, 10),
    };
  return { contractDocumentLink: '', contractDocumentName: '', contractUploadDate: '' };
}

/** String id/code from a selector that may store an object (`departmentId`, `code`, `id`). */
export function extractString(val: unknown): string | undefined {
  if (!val) return undefined;
  if (typeof val === 'string') return val || undefined;
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    const str = (obj.departmentId ?? obj.code ?? obj.status ?? obj.value ?? obj.id) as
      | string
      | undefined;
    return str || undefined;
  }
  if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
    return String(val);
  }
  return undefined;
}

/** Coerce a numeric form field, or undefined. */
export function toNumber(val: unknown): number | undefined {
  if (val === undefined || val === null || val === '') return undefined;
  const n = Number(val);
  return Number.isNaN(n) ? undefined : n;
}

/** Wrap a scalar as a one-item array; empty/null becomes undefined. */
export function toArray(val: unknown): unknown[] | undefined {
  if (val === undefined || val === null) return undefined;
  if (Array.isArray(val)) return val.length > 0 ? val : undefined;
  return [val];
}

/** Flatten onboarding sections into a single form object (later keys overwrite). */
export function mergeSectionValues(sections: Record<string, unknown>): Record<string, any> {
  let merged: Record<string, any> = {};
  for (const sec of Object.values(sections)) {
    if (sec && typeof sec === 'object') {
      merged = { ...merged, ...(sec as Record<string, any>) };
    }
  }
  return merged;
}

/** Remap cert/contract/criminal-record section keys onto form field names. */
function certificateSection(
  sections: Record<string, unknown> | undefined,
): Record<string, any> | undefined {
  const myDocs = sections?.my_documents as Record<string, any> | undefined;
  const legacy = sections?.certification as Record<string, any> | undefined;
  const hasCertificate = (section: Record<string, any> | undefined) =>
    Boolean(
      section?.documentLink ||
      section?.documentName ||
      section?.attestationNumber ||
      section?.obtentionDate,
    );
  if (hasCertificate(myDocs)) return myDocs;
  if (hasCertificate(legacy)) return legacy;
  return undefined;
}

/** Overlay cert / contract / criminal-record section keys onto the merged form. */
export function applyDocumentSectionAliases(
  merged: Record<string, any>,
  sections: Record<string, unknown>,
) {
  const certSec = certificateSection(sections);
  const contractSec = sections.contract as Record<string, any> | undefined;
  const criminalSec = (sections.criminal_record ?? sections.criminalRecord) as
    | Record<string, any>
    | undefined;

  if (certSec?.documentLink) merged.certDocumentLink = certSec.documentLink;
  if (certSec?.documentName) merged.certDocumentName = certSec.documentName;
  if (certSec?.uploadDate) merged.certUploadDate = certSec.uploadDate;
  if (certSec?.attestationNumber != null && certSec.attestationNumber !== '') {
    merged.attestationNumber = certSec.attestationNumber;
  }
  if (certSec?.obtentionDate) merged.obtentionDate = certSec.obtentionDate;

  if (contractSec?.documentLink) merged.contractDocumentLink = contractSec.documentLink;
  if (contractSec?.documentName) merged.contractDocumentName = contractSec.documentName;
  if (contractSec?.uploadDate) merged.contractUploadDate = contractSec.uploadDate;

  // Prefer the dedicated criminal-record section; otherwise keep the already-merged value.
  if (criminalSec?.documentLink) {
    merged.criminalRecordDocumentLink = criminalSec.documentLink;
    if (criminalSec.documentName) merged.criminalRecordDocumentName = criminalSec.documentName;
    if (criminalSec.uploadDate) merged.criminalRecordUploadDate = criminalSec.uploadDate;
  }

  delete merged.documentLink;
  delete merged.documentName;
}

/** Overlay user identity + primary/secondary contact onto the merged form. */
export function applyUserBasics(
  merged: Record<string, any>,
  user: Record<string, any> | null | undefined,
  onboardingStatus: string | undefined,
  defaultCountry: string,
) {
  merged.firstName = merged.firstName ?? user?.firstName ?? '';
  merged.lastName = merged.lastName ?? user?.lastName ?? '';
  merged.status = user?.status ?? '';
  const hydrated = hydrateUserContactFields(
    user?.email,
    user?.phone,
    defaultCountry,
    user?.primaryContactKind,
  );
  merged.contactType = merged.contactType ?? hydrated.contactType;
  if (merged.contactType === 'phone') {
    const parsed = hydratePhoneFromApi(merged.contact || hydrated.contact, defaultCountry);
    merged.contact = parsed.value;
    merged.contactCountryCode = parsed.countryCode;
  } else {
    merged.contact = merged.contact ?? hydrated.contact;
  }
  merged.secondaryContact = hydrated.secondaryContact;
  merged.secondaryContactCountryCode = hydrated.secondaryContactCountryCode;
}

/** Unwrap program/level objects to id arrays so selects hydrate correctly. */
export function normalizeProgramsAndLevels(merged: Record<string, any>) {
  if (merged.programs) {
    merged.programs = Array.isArray(merged.programs)
      ? merged.programs.map((p: any) => (typeof p === 'object' && p.programId ? p.programId : p))
      : [];
  }

  if (Array.isArray(merged.level)) {
    merged.level = merged.level.map((l: any) =>
      typeof l === 'object' && l.levelId ? l.levelId : l,
    );
  } else if (
    merged.level &&
    typeof merged.level === 'object' &&
    'levelId' in (merged.level as object)
  ) {
    merged.level = [(merged.level as { levelId: string }).levelId];
  }

  if (
    merged.levelsToSupport &&
    typeof merged.levelsToSupport === 'object' &&
    'levelId' in merged.levelsToSupport
  ) {
    merged.levelsToSupport =
      (merged.levelsToSupport as { levelId?: unknown }).levelId ?? merged.levelsToSupport;
  }
}

/** Convert API booleans/objects into radio + flattened professional-situation fields. */
export function normalizeRadioAndSituationFields(merged: Record<string, any>) {
  merged.isPartner = formBool(merged.isPartner) ? 'true' : 'false';
  merged.isParticipatedToStudies = formBool(merged.isParticipatedToStudies) ? 'true' : 'false';
  merged.acceptSharePhone = formBool(merged.acceptSharePhone);
  merged.hasParticipated = formBool(merged.hasParticipated);
  if (merged.professionalSituation && typeof merged.professionalSituation === 'string') {
    merged.professionalSituation = { status: merged.professionalSituation };
  }
  if (
    merged.professionalSituation &&
    typeof merged.professionalSituation === 'object' &&
    merged.professionalSituation.status
  ) {
    merged.professionalSituation = flattenProfessionalSituation({
      ...(merged.professionalSituation as Record<string, any>),
    });
  }
}

/** Map legacy GET aliases (`partner`, `desiredStartDate`, `classic`) onto form keys. */
function applyEditFieldAliases(merged: Record<string, any>) {
  if (merged.partner && !merged.partnerName) merged.partnerName = merged.partner;
  if (merged.desiredStartDate && !merged.engagementStartDate) {
    merged.engagementStartDate = merged.desiredStartDate;
  }
  if (merged.teamInformation !== undefined && merged.engagementNotes === undefined) {
    merged.engagementNotes = merged.teamInformation;
  }
  if (merged.coursePath === 'classic') merged.coursePath = 'standard';
  if (merged.targetHours == null && merged.numberOfHoursToComplete != null) {
    merged.targetHours = merged.numberOfHoursToComplete;
  }
}

/** File-name fallback + markValidated from stored criminal-record status. */
function isCriminalRecordMarkedValidated(record: Record<string, any> | undefined): boolean {
  if (!record) return false;
  return (
    record.markValidated === true ||
    record.status === 'validated' ||
    record.validationStatus === 'validated'
  );
}

/** Align criminal-record file name and validated flag. */
function normalizeCriminalRecordEditField(merged: Record<string, any>) {
  if (!merged.criminalRecord || typeof merged.criminalRecord !== 'object') return;
  const criminalRecord = merged.criminalRecord as Record<string, any>;
  if (criminalRecord.documentName && !criminalRecord.fileName) {
    criminalRecord.fileName = criminalRecord.documentName;
  }
  criminalRecord.markValidated = isCriminalRecordMarkedValidated(criminalRecord);
  merged.criminalRecord = criminalRecord;
}

/** Department object wrap, attestation string, and leftover-UUID subject clear. */
export function normalizeMiscEditFields(merged: Record<string, any>) {
  if (merged.department && typeof merged.department === 'string') {
    merged.department = { departmentId: merged.department, departmentName: '' };
  }
  applyEditFieldAliases(merged);
  hydrateRemoteEngagementFields(merged);

  // attestationNumber arrives as a number from the API — the backend expects a string
  if (typeof merged.attestationNumber === 'number') {
    merged.attestationNumber = String(merged.attestationNumber);
  }

  // Backwards-compat: if subjectToTeach is stored as a legacy UUID, clear it
  // so the user picks from the hardcoded REMOTE_SUBJECT_OPTIONS.
  if (merged.subjectToTeach && /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(merged.subjectToTeach)) {
    merged.subjectToTeach = '';
  }

  normalizeCriminalRecordEditField(merged);
}

/** Convert day-keyed `"HH:mm-HH:mm"` slots into `{ weekly }`. */
export function normalizeLegacyAvailabilitySlots(av: Record<string, unknown>) {
  const weekly: Array<{ day: number; start: string; end: string }> = [];
  for (const [dayStr, slots] of Object.entries(av)) {
    const day = Number.parseInt(dayStr, 10);
    if (Number.isNaN(day)) continue;
    const slotArr = Array.isArray(slots) ? (slots as string[]) : [];
    for (const slot of slotArr) {
      const parts = slot.split('-');
      if (parts.length === 2) weekly.push({ day, start: parts[0], end: parts[1] });
    }
  }
  return { weekly };
}

/** Normalize array or legacy day-map availability onto `{ weekly }`. */
export function normalizeAvailabilityField(merged: Record<string, any>) {
  if (Array.isArray(merged.availability)) {
    merged.availability = {
      weekly: (merged.availability as any[]).map((slot: any) => ({
        day: slot.dayOfTheWeek ?? slot.day,
        start: slot.startTime ?? slot.start,
        end: slot.endTime ?? slot.end,
      })),
    };
    return;
  }

  if (!merged.availability || typeof merged.availability !== 'object') return;
  const av = merged.availability as Record<string, unknown>;
  if (av.weekly) return;
  merged.availability = normalizeLegacyAvailabilitySlots(av);
}

/** Hydrate the edit form from GET full-state. */
export function buildEditFormDataFromFullState(
  fullRecord: {
    sections?: Record<string, unknown>;
    user?: Record<string, any>;
    onboardingStatus?: string;
  },
  defaultCountry: string,
): Record<string, any> {
  const sections = fullRecord.sections ?? {};
  const merged = mergeSectionValues(sections);
  applyDocumentSectionAliases(merged, sections);
  applyUserBasics(merged, fullRecord.user, fullRecord.onboardingStatus, defaultCountry);
  normalizeProgramsAndLevels(merged);
  normalizeRadioAndSituationFields(merged);
  normalizeMiscEditFields(merged);
  normalizeAvailabilityField(merged);
  normalizeAvailabilityPills(merged);
  if (!merged.country) merged.country = getCompanyCountry();
  return merged;
}

/** Force the three availability pills to real booleans from the tutor payload. */
export function normalizeAvailabilityPills(merged: Record<string, any>) {
  merged.availableOnVacations = merged.availableOnVacations === true;
  merged.availableOnWeekends = merged.availableOnWeekends === true;
  merged.availableOnHolidays = merged.availableOnHolidays === true;
}

/** Fill address / city / department from an autocomplete pick. */
export function applyRemoteTutorAddressSelection(
  onChange: (key: string, value: unknown) => void,
  result: AddressSelectionResult,
): void {
  const fields = addressFieldsFromSelection(result);
  onChange('address', fields.address);
  onChange('zipCode', fields.zipCode);
  onChange('city', fields.city);
  if (fields.country) onChange('country', fields.country);
  const parsed = parseDepartmentFromLabel(result.department);
  const code = result.departmentCode || parsed?.code;
  if (code) onChange('department', { departmentId: code, departmentName: parsed?.name ?? '' });
}

/** Shared create/update profile fields; skip status when pre-registered (lifecycle owns it). */
export function applySharedTutorProfileFields(
  dto: Record<string, any>,
  formData: Record<string, any>,
  isPreRegistered?: boolean,
) {
  // Only send status when the field is visible (non pre-registered).
  // When hidden, never fall back to the stored value — the lifecycle decides.
  if (!isPreRegistered && formData.status && formData.status !== 'archived') {
    assignTruthy(dto, 'status', formData.status);
  }
  assignTruthy(dto, 'criminalRecord', formData.criminalRecord);
  dto.tutoringType = formData.tutoringType || 'remote';
  assignTruthy(dto, 'coursePath', formData.coursePath);
  assignTruthy(dto, 'programs', formData.programs);
  if (formData.numberOfStudentsToSupport !== undefined) {
    dto.numberOfStudentsToSupport = formData.numberOfStudentsToSupport;
  }
  const weeklyHours = toNumber(formData.targetHours);
  if (weeklyHours !== undefined) {
    dto.targetHours = weeklyHours;
  }
  dto.studentsSupportType = formData.studentsSupportType || 'eleves';
  dto.engagementDurationYears = null;
  if (formData.engagementDurationMonths !== undefined) {
    dto.engagementDurationMonths = formData.engagementDurationMonths;
  }
  const startDateVal = formData.engagementStartDate || formData.desiredStartDate;
  assignTruthy(dto, 'desiredStartDate', startDateVal);
  assignTruthy(dto, 'engagementStartDate', formData.engagementStartDate);
  assignTruthy(dto, 'engagementEndDate', formData.engagementEndDate);
  assignTruthy(dto, 'engagementNotes', formData.engagementNotes);
  const level = toArray(formData.level);
  if (level) dto.level = level;
  assignTruthy(dto, 'subjectToTeach', formData.subjectToTeach);
  dto.availableOnVacations = formData.availableOnVacations === true;
  dto.availableOnWeekends = formData.availableOnWeekends === true;
  dto.availableOnHolidays = formData.availableOnHolidays === true;
  assignTruthy(dto, 'availability', formData.availability);
  assignDefined(dto, 'sessionId', formData.sessionId);
  dto.hasParticipated = formBool(formData.hasParticipated);
  assignTruthy(dto, 'participatedAt', formData.participatedAt);
  if (formData.markedBy !== undefined) dto.markedBy = formData.markedBy;
}

/** Invite channel + email/phone pair from primary/secondary contact. */
export function applyCreateInviteFields(
  dto: Record<string, any>,
  formData: Record<string, any>,
  defaultCountry: string,
) {
  dto.inviteBy = formData.contactType === 'email' ? 'email' : 'phone';
  dto.primaryContactKind = primaryContactKindFromType(formData.contactType);
  dto.firstName = formData.firstName ?? '';
  dto.lastName = formData.lastName ?? '';

  if (formData.contactType === 'email') {
    dto.email = formData.contact ?? '';
    if (formData.secondaryContact?.trim()) {
      dto.phone = phoneToE164(
        formData.secondaryContact,
        formData.secondaryContactCountryCode || defaultCountry,
      );
    }
    return;
  }

  dto.phone = phoneToE164(formData.contact ?? '', formData.contactCountryCode || defaultCountry);
  if (formData.secondaryContact?.trim()) {
    dto.email = formData.secondaryContact.trim();
  }
}

/** Cert / contract / criminal-record URLs for create. */
export function applyCreateDocumentFields(dto: Record<string, any>, formData: Record<string, any>) {
  assignTruthy(dto, 'certDocumentLink', storedDocumentLink(formData.certDocumentLink));
  assignTruthy(dto, 'certDocumentName', formData.certDocumentName);
  assignTruthy(dto, 'attestationNumber', formData.attestationNumber);
  assignTruthy(dto, 'obtentionDate', formData.obtentionDate);
  assignTruthy(dto, 'certUploadDate', formData.certUploadDate);
  assignTruthy(dto, 'contractDocumentLink', storedDocumentLink(formData.contractDocumentLink));
  assignTruthy(dto, 'contractDocumentName', formData.contractDocumentName);
  assignTruthy(dto, 'signedDate', formData.signedDate);
  assignTruthy(dto, 'contractUploadDate', formData.contractUploadDate);
  assignTruthy(
    dto,
    'criminalRecordDocumentLink',
    storedDocumentLink(formData.criminalRecordDocumentLink),
  );
}

/** Build the create-invite profile DTO from form state. */
export function buildCreateTutorDto(formData: Record<string, any>, defaultCountry: string) {
  const dto: Record<string, any> = {};
  applyCreateInviteFields(dto, formData, defaultCountry);
  assignTruthy(dto, 'gender', formData.gender);
  dto.acceptSharePhone = formBool(formData.acceptSharePhone);
  assignTruthy(dto, 'address', formData.address);
  assignTruthy(dto, 'zipCode', formData.zipCode);
  assignTruthy(dto, 'city', formData.city);
  assignTruthy(dto, 'department', extractString(formData.department));
  assignTruthy(dto, 'country', formData.country);
  assignTruthy(dto, 'dateOfBirth', formData.dateOfBirth);
  assignTruthy(
    dto,
    'professionalSituation',
    serializeProfessionalSituation(formData.professionalSituation, 'tutor'),
  );
  // Partner fields stay hidden: do not write a default answer on create.
  dto.isParticipatedToStudies = formBool(formData.isParticipatedToStudies);
  applySharedTutorProfileFields(dto, formData);
  applyCreateDocumentFields(dto, formData);
  return dto;
}

/** Document fields on update: prefer form, fall back to GET section values. */
export function applyUpdateDocumentFallbacks(
  dto: Record<string, any>,
  formData: Record<string, any>,
  sections: Record<string, any> | undefined,
) {
  const certBase = certificateSection(sections);
  assignTruthy(
    dto,
    'certDocumentLink',
    storedDocumentLink(formData.certDocumentLink) || certBase?.documentLink,
  );
  assignTruthy(dto, 'certDocumentName', formData.certDocumentName || certBase?.documentName);
  assignTruthy(dto, 'attestationNumber', formData.attestationNumber || certBase?.attestationNumber);
  assignTruthy(dto, 'obtentionDate', formData.obtentionDate || certBase?.obtentionDate);
  assignTruthy(dto, 'certUploadDate', formData.certUploadDate || certBase?.uploadDate);

  const contractBase = sections?.contract as Record<string, any> | undefined;
  assignTruthy(
    dto,
    'contractDocumentLink',
    storedDocumentLink(formData.contractDocumentLink) || contractBase?.documentLink,
  );
  assignTruthy(
    dto,
    'contractDocumentName',
    formData.contractDocumentName || contractBase?.documentName,
  );
  assignTruthy(dto, 'signedDate', formData.signedDate || contractBase?.signedDate);
  assignTruthy(dto, 'contractUploadDate', formData.contractUploadDate || contractBase?.uploadDate);
}

/** Build the update profile DTO plus criminal-record validate/reset flags. */
export function buildUpdateTutorDto(params: {
  formData: Record<string, any>;
  fullState: any;
  record: TutorRecord;
  defaultCountry: string;
  isPreRegistered: boolean;
}) {
  const { formData, fullState, record, defaultCountry, isPreRegistered } = params;
  const dto: Record<string, any> = {};
  const sections = fullState?.record?.sections as Record<string, any> | undefined;
  const piBase = sections?.personal_information as Record<string, any> | undefined;
  const piGet = (key: string) => formData[key] ?? piBase?.[key];

  assignTruthy(dto, 'firstName', formData.firstName);
  assignTruthy(dto, 'lastName', formData.lastName);
  assignTruthy(dto, 'gender', piGet('gender'));
  if (formData.contactType === 'email' || formData.contactType === 'phone') {
    assignPrimaryContactFields({
      dto,
      primaryValue: String(formData.contact ?? ''),
      primaryKind: formData.contactType,
      primaryCountry: formData.contactCountryCode,
      secondaryValue: formData.secondaryContact,
      secondaryCountry: formData.secondaryContactCountryCode,
      defaultCountry,
    });
  }

  dto.acceptSharePhone = formBool(piGet('acceptSharePhone'));
  assignTruthy(dto, 'address', piGet('address'));
  assignTruthy(dto, 'zipCode', piGet('zipCode'));
  assignTruthy(dto, 'city', piGet('city'));
  assignTruthy(
    dto,
    'department',
    extractString(piGet('department')) || extractString(formData.department),
  );
  assignTruthy(dto, 'country', piGet('country'));
  assignTruthy(dto, 'dateOfBirth', piGet('dateOfBirth'));
  const profSitU = formData.professionalSituation
    ? serializeProfessionalSituation(formData.professionalSituation, 'tutor')
    : piBase?.professionalSituation;
  assignTruthy(dto, 'professionalSituation', profSitU);
  dto.isPartner = piBase?.isPartner ?? record.isPartner;
  dto.partnerName = piBase?.partnerName ?? piBase?.partner;
  dto.isParticipatedToStudies = formBool(piGet('isParticipatedToStudies'));
  applySharedTutorProfileFields(dto, formData, isPreRegistered);
  applyUpdateDocumentFallbacks(
    dto,
    formData,
    sections ?? (record.sections as Record<string, any> | undefined),
  );

  const crBase =
    sections?.my_documents?.criminalRecord ??
    (record.sections as any)?.my_documents?.criminalRecord ??
    (record.sections as any)?.official_documents?.criminalRecord;
  const wasValidated = isCriminalRecordMarkedValidated(crBase);
  const nowValidated = formData.criminalRecord?.markValidated === true;

  return { dto, wasValidated, nowValidated, tutorUserId: record.userId };
}

/** After create: mark the criminal record validated when the checkbox is on. */
export async function applyCriminalRecordOnCreate(
  userId: string | undefined,
  markValidated: boolean,
) {
  if (!userId || !markValidated) return;
  try {
    await updateCriminalRecordDecision(userId, 'validate');
  } catch (err) {
    console.warn('[CreateTutorView] Criminal record decision failed at creation:', err);
  }
}

/** Add N calendar months to a YYYY-MM-DD civil date, clamping the day to the target month. */
export function addCalendarMonthsToIso(startStr: string, months: number): string {
  const start = toCalendarDateString(startStr);
  const date = parseLocalDateString(start);
  const targetMonthIndex = date.getMonth() + months;
  const targetYear = date.getFullYear() + Math.floor(targetMonthIndex / 12);
  const normalizedMonth = ((targetMonthIndex % 12) + 12) % 12;
  const maxDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  date.setFullYear(targetYear, normalizedMonth, Math.min(date.getDate(), maxDay));
  return toLocalDateString(date);
}

/** End date for a standard 3 / 6 / 12-month engagement. */
export function computeEngagementEndDate(startDateStr: string, durationMonths: number): string {
  return addCalendarMonthsToIso(startDateStr, durationMonths);
}

/** Default start date: today in the local calendar (not UTC). */
export function computeDefaultEngagementStartDate(): string {
  return toLocalDateString(new Date());
}

/** Identify an exact 3 / 6 / 12 calendar-month span, otherwise `null` (custom). */
export function deriveEngagementDurationMonths(
  startStr: string | undefined,
  endStr: string | undefined,
): 3 | 6 | 12 | null {
  const start = toCalendarDateString(startStr);
  const end = toCalendarDateString(endStr);
  if (!start || !end) return null;
  for (const months of [3, 6, 12] as const) {
    if (addCalendarMonthsToIso(start, months) === end) return months;
  }
  return null;
}

/** Restore preset vs custom from persisted start/end without recalculating them. */
export function hydrateRemoteEngagementFields(merged: Record<string, any>): void {
  const start = toCalendarDateString(merged.engagementStartDate);
  const end = toCalendarDateString(merged.engagementEndDate);
  if (start) merged.engagementStartDate = start;
  if (end) merged.engagementEndDate = end;

  const derived = deriveEngagementDurationMonths(start || undefined, end || undefined);
  if (derived) {
    merged.engagementMode = 'preset';
    merged.engagementDurationMonths = derived;
    return;
  }
  if (start && end) {
    merged.engagementMode = 'custom';
    merged.engagementDurationMonths = null;
  }
}

/** Label for recap: exact 3 / 6 / 12 months, else historical years, else custom. */
export function formatRemoteEngagementDuration(params: {
  startDate?: string;
  endDate?: string;
  durationYears?: number | null;
  durationMonths?: number | null;
}): string {
  const derived = deriveEngagementDurationMonths(params.startDate, params.endDate);
  if (derived) {
    return (
      ENGAGEMENT_DURATION_OPTIONS.find((option) => option.value === derived)?.label ??
      'Date personnalisée'
    );
  }
  if (params.startDate && params.endDate) {
    if (params.durationYears) return `${params.durationYears} an(s)`;
    return 'Date personnalisée';
  }
  const storedMonthsLabel = ENGAGEMENT_DURATION_OPTIONS.find(
    (option) => option.value === params.durationMonths,
  )?.label;
  if (storedMonthsLabel) return storedMonthsLabel;
  if (params.durationYears) return `${params.durationYears} an(s)`;
  return '—';
}

/** Latest birth date (YYYY-MM-DD) for a remote tutor who is already the minimum age. */
export function getRemoteTutorMaxBirthDate(from = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  date.setFullYear(date.getFullYear() - TUTOR_MIN_AGE);
  return toLocalDateString(date);
}

/** Error when a filled birth date is younger than the remote tutor minimum age. */
export function getTutorMinAgeError(dateOfBirth: string | undefined): string | undefined {
  const calendar = toCalendarDateString(dateOfBirth);
  if (!calendar) return undefined;
  if (calendar > getRemoteTutorMaxBirthDate()) {
    return getErrorMessage({ code: 'tutorMinAge', minAge: TUTOR_MIN_AGE });
  }
  return undefined;
}

/** Error when a newly chosen start date is before today (local calendar). */
export function checkEngagementStartNotInPast(value: string | undefined): string | undefined {
  const calendar = toCalendarDateString(value);
  if (!calendar) return undefined;
  if (calendar < computeDefaultEngagementStartDate()) {
    return getErrorMessage('commitment_start_date_cannot_be_in_the_past');
  }
  return undefined;
}

/** Error when custom start→end is shorter than `minMonths` calendar months. */
export function checkCustomEngagementDurationError(
  startStr: string | undefined,
  endStr: string | undefined,
  minMonths: number,
): string | undefined {
  const start = toCalendarDateString(startStr);
  const end = toCalendarDateString(endStr);
  if (!start || !end) return undefined;
  if (end < addCalendarMonthsToIso(start, minMonths)) {
    return getErrorMessage({ code: 'customEngagementMinMonths', minMonths });
  }
  return undefined;
}

/** Empty create form with remote tutoring defaults. */
export function buildCreateInitialData(defaultCountry: string): Record<string, any> {
  return {
    tutoringType: 'remote',
    availableOnVacations: true,
    availableOnWeekends: false,
    availableOnHolidays: false,
    acceptSharePhone: false,
    isPartner: false,
    isParticipatedToStudies: false,
    hasParticipated: false,
    secondaryContact: '',
    secondaryContactCountryCode: defaultCountry,
    country: getCompanyCountry(),
  };
}

/** Weekly start/end windows unioned from the selected programs. */
export function computeAllowedSlotsFromPrograms(
  programs: any[] | undefined,
  selectedProgramIds: string[] | undefined,
): Array<{ start: string; end: string }> | undefined {
  if (!selectedProgramIds?.length) return undefined;
  const selected = (programs ?? []).filter((p: any) => selectedProgramIds.includes(p.id));
  const slots = selected
    .flatMap((p: any) => p.availability?.weekly ?? [])
    .map((s: any) => ({ start: s.start, end: s.end }));
  return slots.length > 0 ? slots : undefined;
}

/** After edit: validate or reset the criminal-record decision if the checkbox changed. */
export async function applyCriminalRecordOnUpdate(
  tutorUserId: string,
  wasValidated: boolean,
  nowValidated: boolean,
): Promise<void> {
  if (nowValidated === wasValidated) return;
  const action = nowValidated ? 'validate' : 'cancel';
  await updateCriminalRecordDecision(tutorUserId, action);
}

/** Catalog label for a reactivation status code. */
export function getReactivationStatusLabel(status: string): string {
  return TUTOR_ACCOUNT_STATUS_CONFIG[status]?.label ?? status;
}

/** Catalog label for a reactivation situation, or `'—'`. */
export function getReactivationSituationLabel(situation: string | null): string {
  if (!situation) return '—';
  return TUTOR_SITUATION_CONFIG[situation as TutorSituation]?.label ?? situation;
}

/** Current vs next status/situation labels for the reactivation confirm. */
export function formatReactivationPreview(preview: ReactivationPreview): {
  currentStatus: string;
  nextStatus: string;
  currentSituation: string;
  nextSituation: string;
} {
  return formatSharedReactivationPreview(
    preview,
    getReactivationStatusLabel,
    getReactivationSituationLabel,
  );
}

/** Patch one field; changing contact type clears the secondary contact. */
export function applyRemoteTutorFieldUpdate(
  prev: Record<string, any>,
  key: string,
  value: unknown,
  defaultCountry: string,
): Record<string, any> {
  const next = key.includes('.') ? setNestedPath(prev, key, value) : { ...prev, [key]: value };
  if (
    key === 'contactType' &&
    prev.contactType &&
    prev.contactType !== 'unknown' &&
    value !== prev.contactType
  ) {
    next.secondaryContact = '';
    next.secondaryContactCountryCode = defaultCountry;
  }
  return next;
}

/** Hide "already participated" fields while an information-meeting slot is selected. */
export function shouldShowInformationMeetingParticipationFields(formData: {
  sessionId?: unknown;
  hasParticipated?: unknown;
}): boolean {
  return !formData.sessionId || formData.hasParticipated === true;
}

/** Patch information-meeting participation (stamp date + marker on check). */
export function buildInformationMeetingParticipationPatch(
  checked: boolean,
  current: { participatedAt?: string | null; markedBy?: unknown },
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    userRoles?: Array<{ role?: { code?: string } }>;
  } | null,
): Record<string, unknown> {
  if (!checked) {
    return { hasParticipated: false, participatedAt: null, markedBy: null };
  }
  const patch: Record<string, unknown> = { hasParticipated: true };
  if (!current.participatedAt) {
    patch.participatedAt = new Date().toISOString();
  }
  if (user && !current.markedBy) {
    patch.markedBy = {
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      role: getPrimaryRole(user.userRoles)?.code || 'admin',
    };
  }
  return patch;
}

/** Coerce a level field to a string id array. */
export function normalizeTutorLevelFieldToArray(level: unknown): string[] {
  if (level == null || level === '') return [];
  if (Array.isArray(level)) {
    return level.filter((item): item is string => typeof item === 'string');
  }
  return typeof level === 'string' ? [level] : [];
}

/** Preset engagement: duration in calendar months plus computed start/end. */
export function buildEngagementPresetPatch(
  formData: { engagementStartDate?: string },
  months: number,
): Record<string, unknown> {
  const startDate = formData.engagementStartDate || computeDefaultEngagementStartDate();
  return {
    engagementMode: 'preset',
    engagementDurationMonths: months,
    engagementDurationYears: null,
    engagementStartDate: startDate,
    engagementEndDate: computeEngagementEndDate(startDate, months),
  };
}

/** Switch to custom duration; keep start, leave end empty so the user picks it. */
export function buildEngagementCustomTogglePatch(formData: {
  engagementStartDate?: string;
}): Record<string, unknown> {
  return {
    engagementMode: 'custom',
    engagementDurationMonths: null,
    engagementDurationYears: null,
    engagementStartDate: formData.engagementStartDate || computeDefaultEngagementStartDate(),
    engagementEndDate: '',
  };
}

/** Set start date; recompute end when a preset duration is set. */
export function buildEngagementStartDatePatch(
  value: string,
  isCustom: boolean,
  durationMonths: number | undefined,
): Record<string, unknown> {
  const patch: Record<string, unknown> = { engagementStartDate: value };
  if (!isCustom && durationMonths && value) {
    patch.engagementEndDate = computeEngagementEndDate(value, durationMonths);
  }
  return patch;
}

/** Patch contract / cert links; criminal-record only when `includeCriminalRecord` (create, not edit). */
export function mergePendingUploads<T extends DocumentLinkFields>(
  formData: T,
  uploadedUrls: Record<string, string>,
  includeCriminalRecord = true,
): T {
  return {
    ...formData,
    ...(uploadedUrls.contractFile ? { contractDocumentLink: uploadedUrls.contractFile } : {}),
    ...(uploadedUrls.certFile ? { certDocumentLink: uploadedUrls.certFile } : {}),
    ...(includeCriminalRecord && uploadedUrls.criminalRecordFile
      ? { criminalRecordDocumentLink: uploadedUrls.criminalRecordFile }
      : {}),
  };
}
